import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule, MatSelectTrigger } from '@angular/material/select';
import { ICampaignForm } from '@campaign-form/campaign-form.interface';
import { CampaignFormStore } from '@campaign-form/campaign-form.store';
import { PermissionService } from '@core/services/permission.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { EPermissions } from '@enums/permissions.enum';
import { TranslateModule } from '@ngx-translate/core';
import { CreateCustomerDialogComponent } from '@pages/customers/create-customer-dialog/create-customer-dialog.component';
import { ICreateCustomerDialogData } from '@pages/customers/create-customer-dialog/create-customer-dialog.interface';
import { ICustomer } from '@pages/customers/customers.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { uniqBy } from 'lodash';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  imports: [
    IconBadgeComponent,
    IconComponent,
    MatButtonModule,
    MatErrorComponent,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    MatSelectTrigger,
    NgTemplateOutlet,
    NgxMatSelectSearchModule,
    NgxSkeletonLoaderModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'app-campaign-customer',
  templateUrl: './campaign-customer.component.html',
})
export class CampaignCustomerComponent implements OnDestroy {
  public readonly form = input.required<FormGroup<ICampaignForm>>();

  public readonly newCustomers = signal<ICustomer[]>([]);
  public readonly searchTerm = signal<string>('');
  public readonly selectedCustomer = signal<ICustomer | null>(null);

  public readonly customers = computed(() => {
    const uniqueCustomers = uniqBy(
      [...(this.newCustomers()?.length ? this.newCustomers() : []), ...(this.campaignFormStore.customers$() ?? [])],
      'id',
    );

    return uniqueCustomers.sort((a: ICustomer, b: ICustomer) => a.name.localeCompare(b.name));
  });

  public readonly filteredCustomers = computed(() => {
    const term: string = this.searchTerm().toLowerCase();
    const selectedCustomer: ICustomer | null = this.selectedCustomer();

    let filtered = term
      ? this.customers().filter((customer: ICustomer) => customer.name.toLowerCase().includes(term))
      : this.customers();

    if (selectedCustomer) {
      const selectedIndex = filtered.findIndex((customer: ICustomer) => customer.id === selectedCustomer.id);

      if (selectedIndex > -1) {
        filtered = [
          filtered[selectedIndex],
          ...filtered.filter((customer: ICustomer) => customer.id !== selectedCustomer.id),
        ];
      } else {
        filtered = [selectedCustomer, ...filtered];
      }
    }

    return filtered;
  });

  public readonly customerSearchControl: FormControl<string | null> = new FormControl<string | null>('');

  public readonly campaignFormStore = inject(CampaignFormStore);
  private readonly permissionService = inject(PermissionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  private readonly currentUserPermissions = toSignal(this.permissionService.currentUserPermissions$, {
    initialValue: this.permissionService.currentUserPermissions,
  });

  public readonly canReadCustomers = computed(() =>
    this.currentUserPermissions().includes(EPermissions.customerReadOwn),
  );
  public readonly canCreateCustomer = computed(() =>
    this.currentUserPermissions().includes(EPermissions.customerCreate),
  );

  /** True only while the initial fetch is actually outstanding - `customers$()` stays `null`
   * forever (never fetched at all) when `canReadCustomers()` is false, so gating on that too keeps
   * this from showing a skeleton loader forever instead of resolving to "nothing to show". */
  public readonly loading = computed<boolean>(
    () => this.canReadCustomers() && this.campaignFormStore.customers$() === null,
  );

  private customerControlSubscription: Subscription | null = null;

  constructor() {
    this.createCustomerFillEffect();
    this.subscribeToCustomerChanges();
    this.subscribeToSearchChanges();
  }

  public openCustomerCreateDialog(): void {
    this.fillCustomer();

    const data: ICreateCustomerDialogData = {
      customer: null,
      redirectToCustomerDetails: false,
    };

    const dialogRef = this.dialog.open(CreateCustomerDialogComponent, {
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((customer: ICustomer | undefined) => {
        if (!customer) {
          return;
        }

        this.newCustomers.set([customer, ...this.newCustomers()]);
        this.form()?.patchValue({ customer, customerId: customer.id });
      });
  }

  public ngOnDestroy(): void {
    this.customerSearchControl.reset();
  }

  public onCustomerClick(customer: ICustomer, event: Event): void {
    event.stopPropagation();

    WindowRedirectService.openUrlInNewTab(`/customers/${customer.id}`);
  }

  private createCustomerFillEffect(): void {
    effect(() => {
      this.fillCustomer();
    });
  }

  private subscribeToSearchChanges(): void {
    this.customerSearchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((searchValue: string | null) => {
        this.searchTerm.set(searchValue ?? '');
      });
  }

  private subscribeToCustomerChanges(): void {
    effect(() => {
      const customerControl = this.form()?.get('customer');

      if (customerControl) {
        const initialCustomer: ICustomer | null = customerControl.value ?? null;
        this.selectedCustomer.set(initialCustomer);

        this.customerControlSubscription?.unsubscribe();
        this.customerControlSubscription = customerControl.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((customer: ICustomer | null) => {
            this.selectedCustomer.set(customer ?? null);
          });
      }
    });
  }

  private fillCustomer(): void {
    const customers = this.customers() ?? [];
    const formCustomerId = this.form()?.get('customerId')?.value;

    if (customers.length && formCustomerId) {
      const selectedCustomer = customers.find((customer) => customer.id === formCustomerId);

      setTimeout(() => {
        this.form()?.patchValue({ customer: selectedCustomer });
        this.selectedCustomer.set(selectedCustomer ?? null);
      });
    }
  }
}
